import React from 'react';
import { useSelector } from 'react-redux';

import ChatBubbleIcon from 'images/chat-bubble-icon.svg';

import {
    socialChannels,
    voiceChannels,
    webchatChannels,
    taskChannels,
} from 'store/contactHistory/contactHistory.constants';
import { selectContactHistoryInstance } from 'store/contactHistory/contactHistory.selectors';

import { formatInteractionDate } from 'utils';

import InteractionInformation from '../InteractionInformation';
import { constructDisplayName, constructTags, formatInteractionDuration } from '../utils';

import VoiceWidgets from './VoiceWidgets';
import ChatWidgets from './ChatWidgets';
import TaskWidgets from './TaskWidgets';

import './main-view.scss';

const MainView: React.FC<{}> = () => {
    const { selectedInteraction } = useSelector(selectContactHistoryInstance) ?? {};

    return (
        <div className="contact-history-main-view">
            {selectedInteraction ? (
                (() => {
                    const {
                        agentName,
                        connectedToAgentTimestamp,
                        connectedToSystemTimestamp,
                        interactionDuration,
                        channelType,
                        emailMessages,
                        messageId,
                        disconnectReason,
                        queueName,
                        initiationMethod,
                        customerEndpointAddress,
                        attributes
                    } = selectedInteraction;

                    const endpointAddress = constructDisplayName(channelType, customerEndpointAddress, attributes);
                    const agentDisplayName = agentName ?? 'No name found';
                    const connectedTimestamp = connectedToAgentTimestamp ?? connectedToSystemTimestamp;
                    const interactionDate = connectedTimestamp
                        ? formatInteractionDate(connectedTimestamp)
                        : 'No date found';
                    const interactionDisplayDuration = channelType.startsWith('TASK')
                        ? undefined
                        : interactionDuration
                        ? formatInteractionDuration(interactionDuration)
                        : 'No duration found';
                    const { subject, attachments } =
                        emailMessages?.find(emailMessage => emailMessage.messageId === messageId) || {};

                    return (
                        <>
                            <InteractionInformation
                                endpointAddress={endpointAddress}
                                queueName={queueName}
                                role="group"
                                agentName={agentDisplayName}
                                date={interactionDate}
                                channelType={channelType}
                                tags={constructTags(selectedInteraction)}
                                duration={interactionDisplayDuration}
                                subject={subject}
                                disabled
                                hasAttachments={!!attachments?.length}
                                disconnectWithNoAgentReason={!agentName ? disconnectReason : undefined}
                                initiationMethod={initiationMethod}
                            />
                            {voiceChannels.includes(channelType) && <VoiceWidgets interaction={selectedInteraction} />}
                            {(socialChannels.includes(channelType) || webchatChannels.includes(channelType)) && (
                                <ChatWidgets interaction={selectedInteraction} />
                            )}
                            {taskChannels.includes(channelType) && <TaskWidgets interaction={selectedInteraction} />}
                        </>
                    );
                })()
            ) : (
                <div className="contact-history-main-view-no-selected-interaction">
                    <img src={ChatBubbleIcon} alt="Chat bubble" />
                    <p className="contact-history-main-view-no-selected-interaction-message">
                        Nothing is selected, please select an item
                        <br />
                        from the left panel to see more
                    </p>
                </div>
            )}
        </div>
    );
};

export default MainView;
